import { MissionKindEnum } from '@e-socna/data-layer';

import { MissionSpaceEnum } from '../enums/mission-space.enum';
import type { BreadcrumbPath } from '../../components/Breadcrumbs';

import { missionKindProfileOf } from './mission-kind-profile.util';
import { isSubMissionSpace } from './mission-space.util';
import type { MissionSpacePath } from './mission-space.util';
import type { MissionWording } from './mission-kind-profile.util';

const COLLABORATIONS_SPACE: MissionSpacePath = MissionSpaceEnum.Collaborations;

// The kind of the mission of a page: the loaded mission, otherwise its space.
export const resolvePageMissionKind = (
  kind: MissionKindEnum | undefined,
  space: MissionSpacePath,
): MissionKindEnum => {
  if (kind) {
    return kind;
  }

  if (isSubMissionSpace(space)) {
    return MissionKindEnum.SubMission;
  }

  return space === COLLABORATIONS_SPACE
    ? MissionKindEnum.Composite
    : MissionKindEnum.Standard;
};

export const missionWordingOf = (kind: MissionKindEnum): MissionWording =>
  missionKindProfileOf(kind).wording;

// The breadcrumb of a mission page up to the mission itself; a sub-mission
// is reached through its collaboration and the sub-missions of it.
export const buildMissionBreadcrumbPaths = ({
  kind,
  paths,
}: Readonly<{
  kind: MissionKindEnum;
  paths: Readonly<{
    folders: string;
    folder: string;
    folderMissions: string;
    mission: string;
    // The collaboration of a sub-mission, unknown while it loads.
    collaboration?: string;
    collaborationSubMissions?: string;
  }>;
}>): readonly BreadcrumbPath[] => {
  const folderPaths: readonly BreadcrumbPath[] = [
    { to: paths.folders, label: 'dossiers', key: 'folders' },
    { to: paths.folder, label: 'dossier', key: 'folder' },
    { to: paths.folderMissions, label: 'missions', key: 'missions' },
  ];

  if (
    missionKindProfileOf(kind).isWithinACollaboration &&
    paths.collaboration &&
    paths.collaborationSubMissions
  ) {
    return [
      ...folderPaths,
      { to: paths.collaboration, label: 'collaboration', key: 'collaboration' },
      {
        to: paths.collaborationSubMissions,
        label: 'sous-missions',
        key: 'subMissions',
      },
      { to: paths.mission, label: 'sous-mission', key: 'mission' },
    ];
  }

  return [
    ...folderPaths,
    {
      to: paths.mission,
      label: missionWordingOf(kind).name.toLowerCase(),
      key: 'mission',
    },
  ];
};
